import { SHOW_IF_LIMITS, } from '../types/survey';
/*
 * Which questions a person is asked.
 *
 * One function decides it, `visibleQuestionIds`, and the form, the submit and the
 * readers all call it, for the same reason `validateAnswer` is single: a client
 * that skips the form must not be able to answer what the form would not have
 * asked, and a form must not ask what the server will then ignore.
 */
function isBlank(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
function sameValue(answer, expected) {
    return answer === expected;
}
/**
 * One condition against what a person has answered, by code.
 *
 * A question that has not been answered satisfies nothing except `not_answered`:
 * "the answer is not X" is not true of an answer that does not exist yet, and a
 * page that opened before its question was answered would flicker open and shut.
 */
export function conditionHolds(condition, answer) {
    if (condition.op === 'not_answered')
        return isBlank(answer);
    if (isBlank(answer))
        return false;
    if (condition.op === 'answered')
        return true;
    const expected = condition.value;
    const picked = Array.isArray(answer) ? answer : [answer];
    const inList = () => Array.isArray(expected) && picked.some((entry) => expected.includes(entry));
    switch (condition.op) {
        case 'equals':
            return picked.some((entry) => sameValue(entry, expected));
        case 'not_equals':
            return !picked.some((entry) => sameValue(entry, expected));
        case 'in':
            return inList();
        case 'not_in':
            return !inList();
        case 'gte':
            return typeof answer === 'number' && typeof expected === 'number' && answer >= expected;
        case 'lte':
            return typeof answer === 'number' && typeof expected === 'number' && answer <= expected;
        default:
            return false;
    }
}
export function showIfHolds(showIf, byCode) {
    if (!showIf || !Array.isArray(showIf.conditions) || showIf.conditions.length === 0)
        return true;
    const results = showIf.conditions.map((condition) => conditionHolds(condition, byCode.get(condition.question)));
    return showIf.match === 'any' ? results.some(Boolean) : results.every(Boolean);
}
/**
 * The ids of the questions that are asked, given what has been answered so far.
 *
 * Questions are taken in order, because a condition only looks back. A question
 * that is not asked counts as unanswered for whoever depends on it, even if a
 * stale answer to it is still stored: a rule hangs off what the person can see.
 * A section that is not shown takes everything under it, up to the next section.
 */
export function visibleQuestionIds(questions, answers) {
    const ordered = [...questions].sort((a, b) => a.order_index - b.order_index);
    const byCode = new Map();
    const visible = new Set();
    let sectionShown = true;
    for (const question of ordered) {
        const own = showIfHolds(question.config?.showIf, byCode);
        if (question.type === 'section') {
            sectionShown = own;
            if (own)
                visible.add(question.id);
            continue;
        }
        const shown = sectionShown && own;
        if (shown)
            visible.add(question.id);
        if (question.code)
            byCode.set(question.code, shown ? answers[question.id] : null);
    }
    return visible;
}
/**
 * What of these answers is kept: only the questions that are asked, and never a
 * heading. A person can answer a question and then change an answer that made it
 * appear; what they wrote there is no longer part of what they said.
 */
export function askedAnswers(questions, answers) {
    const asked = visibleQuestionIds(questions, answers);
    const headings = new Set(questions.filter((q) => q.type === 'section').map((q) => q.id));
    return Object.fromEntries(Object.entries(answers).filter(([id]) => asked.has(id) && !headings.has(id)));
}
/** The ids of the questions that are not asked (headings excluded: they hold no answer). */
export function skippedQuestionIds(questions, answers) {
    const asked = visibleQuestionIds(questions, answers);
    return questions.filter((q) => q.type !== 'section' && !asked.has(q.id)).map((q) => q.id);
}
// ============================================================
// Writing a condition
// ============================================================
const OPS_BY_TYPE = {
    single_choice: ['equals', 'not_equals', 'in', 'not_in', 'answered', 'not_answered'],
    multi_choice: ['in', 'not_in', 'answered', 'not_answered'],
    number: ['equals', 'not_equals', 'gte', 'lte', 'answered', 'not_answered'],
    scale: ['equals', 'not_equals', 'gte', 'lte', 'answered', 'not_answered'],
    boolean: ['equals', 'not_equals', 'answered', 'not_answered'],
    short_text: ['answered', 'not_answered'],
    long_text: ['answered', 'not_answered'],
    date: ['answered', 'not_answered'],
};
/** What a rule may ask of a question of this type, or none when it cannot be looked at. */
export function conditionOpsFor(type) {
    return OPS_BY_TYPE[type] ?? [];
}
/**
 * Whether the conditions in a list of questions can work: each one names a
 * question that exists, comes earlier, and can be asked that thing. Checked on
 * every write, so a rule never points at nothing — and since it can only look
 * back, two rules can never wait on each other.
 */
export function showIfError(questions) {
    for (const [index, question] of questions.entries()) {
        const showIf = question.config?.showIf;
        if (showIf === undefined)
            continue;
        const name = `question ${index + 1}`;
        if (typeof showIf !== 'object' || showIf === null || !Array.isArray(showIf.conditions)) {
            return `${name} has a showIf that is not { conditions: [...] }`;
        }
        if (showIf.match !== undefined && showIf.match !== 'all' && showIf.match !== 'any') {
            return `${name}: showIf.match must be "all" or "any"`;
        }
        if (showIf.conditions.length === 0 || showIf.conditions.length > SHOW_IF_LIMITS.maxConditions) {
            return `${name}: showIf needs 1 to ${SHOW_IF_LIMITS.maxConditions} conditions`;
        }
        for (const condition of showIf.conditions) {
            const problem = conditionError(condition, questions, index);
            if (problem)
                return `${name}: ${problem}`;
        }
    }
    return null;
}
function conditionError(condition, questions, index) {
    if (typeof condition !== 'object' || condition === null)
        return 'a condition is not an object';
    if (typeof condition.question !== 'string' || !condition.question) {
        return 'a condition needs the code of the question it looks at';
    }
    const sourceIndex = questions.findIndex((candidate) => candidate.code === condition.question);
    if (sourceIndex === -1) {
        return `no question has the code "${condition.question}" (a question needs a code to be looked at)`;
    }
    if (sourceIndex >= index) {
        return `"${condition.question}" does not come before it: a condition can only look back`;
    }
    const source = questions[sourceIndex];
    const allowed = OPS_BY_TYPE[source.type];
    if (!allowed)
        return `"${condition.question}" is a ${source.type}, which cannot be looked at`;
    if (!allowed.includes(condition.op)) {
        return `"${condition.op}" does not apply to ${source.type} "${condition.question}" (use ${allowed.join(', ')})`;
    }
    if (condition.op === 'answered' || condition.op === 'not_answered') {
        return condition.value === undefined ? null : `"${condition.op}" takes no value`;
    }
    return valueError(condition, source);
}
function valueError(condition, source) {
    const { op, value } = condition;
    const options = source.config?.options?.map((option) => option.value);
    const knownOption = (entry) => typeof entry === 'string' && (!options || options.length === 0 || options.includes(entry));
    if (op === 'gte' || op === 'lte') {
        return typeof value === 'number' && Number.isFinite(value)
            ? null
            : `"${op}" needs a number`;
    }
    if (op === 'in' || op === 'not_in') {
        if (!Array.isArray(value) || value.length === 0 || value.length > 50) {
            return `"${op}" needs a list of 1 to 50 values`;
        }
        if (source.type === 'single_choice' || source.type === 'multi_choice') {
            const unknown = value.find((entry) => !knownOption(entry));
            return unknown === undefined ? null : `"${String(unknown)}" is not an option of "${condition.question}"`;
        }
        return value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
            ? null
            : `"${op}" needs numbers for ${source.type} "${condition.question}"`;
    }
    // equals / not_equals
    if (source.type === 'boolean') {
        return typeof value === 'boolean' ? null : `"${op}" needs true or false`;
    }
    if (source.type === 'number' || source.type === 'scale') {
        return typeof value === 'number' && Number.isFinite(value) ? null : `"${op}" needs a number`;
    }
    return knownOption(value) ? null : `${JSON.stringify(value)} is not an option of "${condition.question}"`;
}
